import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard, FolderKanban, Film, Pen, Mail, Link2, FileText, FlaskConical,
  CalendarDays, Share2, Users, Flame, BarChart3, Archive, Palette, Shield, Plus,
  Trash2, RefreshCw, Copy, Check, ChevronRight, Clock, Zap, Target, Send,
  Video, Type, Megaphone, TrendingUp, Eye, Loader2, Play, Download, X,
  UserPlus, UserMinus, Settings, QrCode, Repeat, MessageSquare, Sparkles
} from 'lucide-react';
import CommandCenter from '../components/suite/CommandCenter';
import CampaignsPanel from '../components/suite/CampaignsPanel';
import DesignStudio from '../components/suite/DesignStudio';
import QRStudio from '../components/suite/QRStudio';
import LinksPanel from '../components/suite/LinksPanel';
import StrategyPanel from '../components/suite/StrategyPanel';
import AudiencePanel from '../components/suite/AudiencePanel';
import EmailStudio from '../components/suite/EmailStudio';
import SequencesPanel from '../components/suite/SequencesPanel';
import SocialPanel from '../components/suite/SocialPanel';
import SmsPanel from '../components/suite/SmsPanel';
import PagesPanel from '../components/suite/PagesPanel';
import AnalyticsPanel from '../components/suite/AnalyticsPanel';
import ApprovalsPanel from '../components/suite/ApprovalsPanel';
import IntegrationsPanel from '../components/suite/IntegrationsPanel';
import AssetLibraryPanel from '../components/suite/AssetLibraryPanel';
import { suiteAPI, marketingAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';

const NAV_ITEMS = [
  { id: 'dashboard', label: 'Command Center', icon: LayoutDashboard, group: 'Overview' },
  { id: 'campaigns', label: 'Campaigns', icon: Megaphone, group: 'Overview' },
  { id: 'calendar', label: 'Content Calendar', icon: CalendarDays, group: 'Overview' },
  { id: 'projects', label: 'Projects', icon: FolderKanban, group: 'Overview' },

  { id: 'design', label: 'Design Studio', icon: Palette, group: 'Create' },
  { id: 'video-ads', label: 'Sora 2 Video Ads', icon: Film, group: 'Create' },
  { id: 'ai-copy', label: 'AI Copywriter', icon: Pen, group: 'Create' },
  { id: 'qr', label: 'QR Codes', icon: QrCode, group: 'Create' },
  { id: 'assets', label: 'Asset Library', icon: Archive, group: 'Create' },
  { id: 'brand-kit', label: 'Brand Kit', icon: Sparkles, group: 'Create' },

  { id: 'email', label: 'Email Campaigns', icon: Mail, group: 'Distribute' },
  { id: 'sequences', label: 'Email Sequences', icon: Repeat, group: 'Distribute' },
  { id: 'social', label: 'Social Studio', icon: Share2, group: 'Distribute' },
  { id: 'sms', label: 'SMS Blasts', icon: MessageSquare, group: 'Distribute' },
  { id: 'pages', label: 'Landing Pages', icon: FileText, group: 'Distribute' },
  { id: 'links', label: 'Smart Links', icon: Link2, group: 'Distribute' },

  { id: 'analytics', label: 'Analytics & ROI', icon: BarChart3, group: 'Intelligence' },
  { id: 'strategy', label: 'Strategy Room', icon: FlaskConical, group: 'Intelligence' },
  { id: 'audience', label: 'Audience & Leads', icon: Users, group: 'Intelligence' },

  { id: 'approvals', label: 'Approvals', icon: Flame, group: 'Operations' },
  { id: 'team', label: 'Team', icon: Shield, group: 'Operations' },
  { id: 'integrations', label: 'Integrations', icon: Settings, group: 'Operations' },
];

export default function MarketingSuitePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [hasAccess, setHasAccess] = useState(null);
  const [isSuperadmin, setIsSuperadmin] = useState(false);
  const [activeSection, setActiveSection] = useState('dashboard');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [campaigns, setCampaigns] = useState([]);

  useEffect(() => {
    suiteAPI.checkAccess().then(r => {
      setHasAccess(r.data.has_access);
      setIsSuperadmin(r.data.is_superadmin);
      if (r.data.has_access) suiteAPI.listCampaigns().then(c => setCampaigns(c.data)).catch(() => {});
      if (r.data.has_access) suiteAPI.setPublicBase(window.location.origin).catch(() => {});
    }).catch(() => setHasAccess(false));
  }, []);

  if (hasAccess === null) return <div className="min-h-screen bg-zinc-950 flex items-center justify-center"><Loader2 className="animate-spin text-[#b8ff00]" size={32} /></div>;
  if (!hasAccess) return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-8" data-testid="suite-access-denied">
      <div className="text-center max-w-md">
        <Shield size={48} className="mx-auto mb-4 text-red-400" />
        <h1 className="text-2xl font-bold text-white mb-2">Private Access Only</h1>
        <p className="text-zinc-400 mb-6">This Marketing Suite is restricted to authorized team members.</p>
        <Button onClick={() => navigate('/')} className="bg-zinc-800 text-white">Go Home</Button>
      </div>
    </div>
  );

  const ActivePanel = {
    'dashboard': CommandCenter,
    'campaigns': CampaignsPanel,
    'calendar': CalendarPanel,
    'projects': ProjectsPanel,
    'design': DesignStudio,
    'video-ads': VideoAdsPanel,
    'ai-copy': AICopyPanel,
    'qr': QRStudio,
    'assets': AssetLibraryPanel,
    'brand-kit': BrandKitPanel,
    'email': EmailStudio,
    'sequences': SequencesPanel,
    'social': SocialPanel,
    'sms': SmsPanel,
    'pages': PagesPanel,
    'links': LinksPanel,
    'analytics': AnalyticsPanel,
    'strategy': StrategyPanel,
    'audience': AudiencePanel,
    'approvals': ApprovalsPanel,
    'team': TeamPanel,
    'integrations': IntegrationsPanel,
  }[activeSection] || CommandCenter;

  return (
    <div className="min-h-screen bg-zinc-950 flex" data-testid="marketing-suite">
      {/* Sidebar */}
      <aside className={`${sidebarCollapsed ? 'w-16' : 'w-56'} bg-zinc-900 border-r border-zinc-800 flex flex-col transition-all duration-200 shrink-0`}>
        <div className="p-4 border-b border-zinc-800 flex items-center gap-2">
          {!sidebarCollapsed && (
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#b8ff00] rounded-lg flex items-center justify-center"><span className="font-bold text-black text-sm">M</span></div>
              <div><p className="text-white font-bold text-sm leading-tight">Marketing</p><p className="text-zinc-500 text-[10px]">Suite</p></div>
            </div>
          )}
          <button onClick={() => setSidebarCollapsed(!sidebarCollapsed)} className="ml-auto text-zinc-500 hover:text-white" data-testid="sidebar-toggle">
            <ChevronRight size={16} className={`transition-transform ${sidebarCollapsed ? '' : 'rotate-180'}`} />
          </button>
        </div>
        <nav className="flex-1 py-2 overflow-y-auto">
          {NAV_ITEMS.map((item, i) => (
            <React.Fragment key={item.id}>
              {!sidebarCollapsed && NAV_ITEMS[i - 1]?.group !== item.group && (
                <p className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-widest text-zinc-600">{item.group}</p>
              )}
              <button
                onClick={() => setActiveSection(item.id)}
                className={`w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors ${activeSection === item.id ? 'bg-[#b8ff00]/10 text-[#b8ff00] border-r-2 border-[#b8ff00]' : 'text-zinc-400 hover:text-white hover:bg-zinc-800'}`}
                data-testid={`nav-${item.id}`}
              >
                <item.icon size={17} className="shrink-0" />
                {!sidebarCollapsed && <span className="truncate text-[13px]">{item.label}</span>}
              </button>
            </React.Fragment>
          ))}
        </nav>
        {!sidebarCollapsed && (
          <div className="p-3 border-t border-zinc-800">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 bg-zinc-700 rounded-full flex items-center justify-center"><span className="text-white text-xs font-bold">{user?.name?.[0] || 'M'}</span></div>
              <div className="flex-1 min-w-0"><p className="text-white text-xs font-medium truncate">{user?.name}</p><p className="text-zinc-500 text-[10px] truncate">{isSuperadmin ? 'Admin' : 'Team'}</p></div>
            </div>
          </div>
        )}
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">
        <AnimatePresence mode="wait">
          <motion.div key={activeSection} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
            <ActivePanel isSuperadmin={isSuperadmin} setActiveSection={setActiveSection} campaigns={campaigns} onCampaignsChange={setCampaigns} />
          </motion.div>
        </AnimatePresence>
      </main>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════ */
/*  DASHBOARD                                                      */
/* ═══════════════════════════════════════════════════════════════ */
function DashboardPanel({ setActiveSection }) {
  const [stats, setStats] = useState(null);
  useEffect(() => { suiteAPI.dashboard().then(r => setStats(r.data)).catch(() => {}); }, []);

  if (!stats) return <PanelLoader />;

  const cards = [
    { label: 'Projects', value: stats.total_projects, icon: FolderKanban, color: 'text-blue-400', bg: 'bg-blue-500/10', nav: 'projects' },
    { label: 'Video Ads', value: stats.total_video_ads, icon: Film, color: 'text-rose-400', bg: 'bg-rose-500/10', nav: 'video-ads' },
    { label: 'Generating', value: stats.generating_ads, icon: Loader2, color: 'text-amber-400', bg: 'bg-amber-500/10', nav: 'video-ads' },
    { label: 'AI Copy', value: stats.total_copy, icon: Pen, color: 'text-violet-400', bg: 'bg-violet-500/10', nav: 'ai-copy' },
    { label: 'Assets', value: stats.total_assets, icon: Archive, color: 'text-emerald-400', bg: 'bg-emerald-500/10', nav: 'assets' },
    { label: 'Calendar', value: stats.total_calendar, icon: CalendarDays, color: 'text-cyan-400', bg: 'bg-cyan-500/10', nav: 'calendar' },
    { label: 'Campaigns', value: stats.total_campaigns, icon: Megaphone, color: 'text-orange-400', bg: 'bg-orange-500/10' },
    { label: 'Completed Ads', value: stats.completed_ads, icon: Check, color: 'text-[#b8ff00]', bg: 'bg-[#b8ff00]/10', nav: 'video-ads' },
  ];

  return (
    <div className="p-6 space-y-6" data-testid="suite-dashboard">
      <div>
        <h1 className="text-2xl font-bold text-white">Marketing Suite</h1>
        <p className="text-zinc-500 text-sm">Your private command center</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {cards.map(c => (
          <button key={c.label} onClick={() => c.nav && setActiveSection(c.nav)} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 text-left hover:border-zinc-700 transition-colors" data-testid={`stat-${c.label.toLowerCase().replace(/\s/g, '-')}`}>
            <div className="flex items-center gap-2 mb-2"><div className={`w-8 h-8 ${c.bg} rounded-lg flex items-center justify-center`}><c.icon size={16} className={c.color} /></div></div>
            <p className="text-2xl font-bold text-white">{c.value}</p>
            <p className="text-xs text-zinc-500 uppercase tracking-wider">{c.label}</p>
          </button>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <QuickAction icon={Film} label="Generate Video Ad" desc="Create a Sora 2 ad from a brief" onClick={() => setActiveSection('video-ads')} color="bg-rose-500/10 text-rose-400" />
        <QuickAction icon={Pen} label="AI Copywriter" desc="Headlines, emails, social posts" onClick={() => setActiveSection('ai-copy')} color="bg-violet-500/10 text-violet-400" />
        <QuickAction icon={FolderKanban} label="New Project" desc="Group campaigns & assets" onClick={() => setActiveSection('projects')} color="bg-blue-500/10 text-blue-400" />
      </div>

      {/* Recent Activity */}
      {stats.recent_ads?.length > 0 && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
          <h3 className="text-sm font-semibold text-white mb-3">Recent Video Ads</h3>
          <div className="space-y-2">
            {stats.recent_ads.map(ad => (
              <div key={ad.id} className="flex items-center justify-between p-2 bg-zinc-800/50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Film size={14} className="text-rose-400" />
                  <span className="text-sm text-white">{ad.title || 'Untitled'}</span>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${ad.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' : ad.status === 'generating' ? 'bg-amber-500/20 text-amber-400' : 'bg-red-500/20 text-red-400'}`}>{ad.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function QuickAction({ icon: Icon, label, desc, onClick, color }) {
  return (
    <button onClick={onClick} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 text-left hover:border-zinc-700 transition-colors group">
      <div className={`w-10 h-10 ${color} rounded-xl flex items-center justify-center mb-3`}><Icon size={20} /></div>
      <p className="font-semibold text-white text-sm group-hover:text-[#b8ff00] transition-colors">{label}</p>
      <p className="text-xs text-zinc-500 mt-0.5">{desc}</p>
    </button>
  );
}

/* ═══════════════════════════════════════════════════════════════ */
/*  PROJECTS                                                       */
/* ═══════════════════════════════════════════════════════════════ */
function ProjectsPanel() {
  const [projects, setProjects] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', budget: 0, tags: '' });
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => { suiteAPI.listProjects().then(r => { setProjects(r.data); setLoading(false); }).catch(() => setLoading(false)); }, []);
  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.name.trim()) { toast.error('Project name required'); return; }
    try {
      await suiteAPI.createProject({ ...form, tags: form.tags.split(',').map(t => t.trim()).filter(Boolean) });
      toast.success('Project created');
      setShowCreate(false);
      setForm({ name: '', description: '', budget: 0, tags: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="p-6 space-y-4" data-testid="suite-projects">
      <div className="flex items-center justify-between">
        <div><h2 className="text-xl font-bold text-white">Marketing Projects</h2><p className="text-zinc-500 text-sm">Group campaigns, ads, and assets together</p></div>
        <Button onClick={() => setShowCreate(true)} className="bg-[#b8ff00] text-black font-bold" data-testid="create-project-btn"><Plus size={16} className="mr-1" /> New Project</Button>
      </div>

      {showCreate && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 space-y-3">
          <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Project name (e.g. Q3 Product Launch)" className="bg-zinc-800 border-zinc-700 text-white" data-testid="project-name-input" />
          <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Description..." rows={2} className="bg-zinc-800 border-zinc-700 text-white" />
          <div className="flex gap-2">
            <Input type="number" value={form.budget} onChange={e => setForm({ ...form, budget: parseFloat(e.target.value) || 0 })} placeholder="Budget ($)" className="bg-zinc-800 border-zinc-700 text-white w-32" />
            <Input value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} placeholder="Tags (comma separated)" className="bg-zinc-800 border-zinc-700 text-white flex-1" />
          </div>
          <div className="flex gap-2">
            <Button onClick={handleCreate} className="bg-[#b8ff00] text-black font-bold">Create</Button>
            <Button variant="ghost" onClick={() => setShowCreate(false)} className="text-zinc-400">Cancel</Button>
          </div>
        </div>
      )}

      {loading ? <PanelLoader /> : projects.length === 0 ? (
        <EmptyState icon={FolderKanban} text="No projects yet. Create your first marketing project." />
      ) : (
        <div className="grid gap-3">
          {projects.map(p => (
            <div key={p.id} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 hover:border-zinc-700 transition-colors" data-testid={`project-${p.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-bold text-white">{p.name}</h3>
                  <p className="text-xs text-zinc-500 mt-0.5">{p.description || 'No description'}</p>
                  {p.tags?.length > 0 && <div className="flex gap-1 mt-2">{p.tags.map(t => <span key={t} className="text-[10px] px-2 py-0.5 bg-zinc-800 text-zinc-400 rounded-full">{t}</span>)}</div>}
                </div>
                <div className="flex items-center gap-2">
                  {p.budget > 0 && <span className="text-xs text-emerald-400 font-medium">${p.budget}</span>}
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${p.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-700 text-zinc-400'}`}>{p.status}</span>
                  <Button variant="ghost" size="sm" onClick={async () => { await suiteAPI.deleteProject(p.id); load(); toast.success('Deleted'); }} className="text-zinc-500 hover:text-red-400 h-7 w-7 p-0"><Trash2 size={14} /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════ */
/*  VIDEO ADS (Sora 2)                                             */
/* ═══════════════════════════════════════════════════════════════ */
function VideoAdsPanel() {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGen, setShowGen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [form, setForm] = useState({
    title: '', brief: '', duration_type: 'short', duration: 10, style: 'cinematic', aspect: 'landscape',
  });

  const load = useCallback(() => { suiteAPI.listVideoAds().then(r => { setAds(r.data); setLoading(false); }).catch(() => setLoading(false)); }, []);
  useEffect(() => { load(); const interval = setInterval(load, 8000); return () => clearInterval(interval); }, [load]);

  const handleGenerate = async () => {
    if (!form.brief.trim()) { toast.error('Brief/prompt required'); return; }
    setGenerating(true);
    try {
      await suiteAPI.generateVideoAd(form);
      toast.success('Video ad generation started! This takes 2-5 minutes.');
      setShowGen(false);
      setForm({ title: '', brief: '', duration_type: 'short', duration: 10, style: 'cinematic', aspect: 'landscape' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to start generation'); }
    setGenerating(false);
  };

  const STYLES = ['cinematic', 'social', 'minimal', 'energetic', 'corporate', 'storytelling'];
  const ASPECTS = [{ v: 'landscape', l: '16:9 Landscape' }, { v: 'portrait', l: '9:16 Portrait' }, { v: 'square', l: '1:1 Square' }];

  return (
    <div className="p-6 space-y-4" data-testid="suite-video-ads">
      <div className="flex items-center justify-between">
        <div><h2 className="text-xl font-bold text-white">Video Ad Studio</h2><p className="text-zinc-500 text-sm">Generate video ads with Sora 2 AI</p></div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} className="border-zinc-700 text-zinc-300"><RefreshCw size={14} className="mr-1" /> Refresh</Button>
          <Button onClick={() => setShowGen(true)} className="bg-[#b8ff00] text-black font-bold" data-testid="gen-video-btn"><Plus size={16} className="mr-1" /> New Video Ad</Button>
        </div>
      </div>

      {showGen && (
        <div className="bg-zinc-900 border border-[#b8ff00]/30 rounded-xl p-5 space-y-4">
          <h3 className="font-bold text-white flex items-center gap-2"><Film size={18} className="text-[#b8ff00]" /> Create Video Ad</h3>
          <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Ad title (e.g. Summer Sale Promo)" className="bg-zinc-800 border-zinc-700 text-white" data-testid="video-title-input" />
          <Textarea value={form.brief} onChange={e => setForm({ ...form, brief: e.target.value })} placeholder="Describe your video ad... (e.g. A fast-paced montage of urban street fashion with bold text overlays showing 50% OFF, ending with our logo)" rows={4} className="bg-zinc-800 border-zinc-700 text-white" data-testid="video-brief-input" />
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-zinc-400 block mb-1">Duration</label>
              <select value={form.duration_type} onChange={e => setForm({ ...form, duration_type: e.target.value, duration: e.target.value === 'short' ? 10 : 30 })} className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm rounded-lg px-3 py-2" data-testid="duration-type-select">
                <option value="short">Short (5-15s)</option>
                <option value="long">Long (16-60s)</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-zinc-400 block mb-1">Exact ({form.duration_type === 'short' ? '5-15' : '16-60'}s)</label>
              <Input type="number" min={form.duration_type === 'short' ? 5 : 16} max={form.duration_type === 'short' ? 15 : 60} value={form.duration} onChange={e => setForm({ ...form, duration: parseInt(e.target.value) || 10 })} className="bg-zinc-800 border-zinc-700 text-white" data-testid="duration-input" />
            </div>
            <div>
              <label className="text-xs text-zinc-400 block mb-1">Aspect Ratio</label>
              <select value={form.aspect} onChange={e => setForm({ ...form, aspect: e.target.value })} className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm rounded-lg px-3 py-2" data-testid="aspect-select">
                {ASPECTS.map(a => <option key={a.v} value={a.v}>{a.l}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs text-zinc-400 block mb-2">Style</label>
            <div className="flex flex-wrap gap-2">
              {STYLES.map(s => (
                <button key={s} onClick={() => setForm({ ...form, style: s })} className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize transition-colors ${form.style === s ? 'bg-[#b8ff00] text-black' : 'bg-zinc-800 text-zinc-400 hover:text-white'}`} data-testid={`style-${s}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button onClick={handleGenerate} disabled={generating} className="bg-[#b8ff00] text-black font-bold" data-testid="start-gen-btn">
              {generating ? <><Loader2 size={14} className="animate-spin mr-1" /> Starting...</> : <><Zap size={14} className="mr-1" /> Generate Video</>}
            </Button>
            <Button variant="ghost" onClick={() => setShowGen(false)} className="text-zinc-400">Cancel</Button>
          </div>
        </div>
      )}

      {loading ? <PanelLoader /> : ads.length === 0 ? (
        <EmptyState icon={Film} text="No video ads yet. Generate your first Sora 2 ad!" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {ads.map(ad => (
            <div key={ad.id} className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden" data-testid={`video-ad-${ad.id}`}>
              {ad.status === 'completed' ? (
                <div className="aspect-video bg-black relative">
                  <video src={suiteAPI.videoAdFileUrl(ad.id)} controls className="w-full h-full object-contain" data-testid={`video-player-${ad.id}`} />
                </div>
              ) : (
                <div className="aspect-video bg-zinc-800 flex items-center justify-center">
                  {ad.status === 'generating' ? (
                    <div className="text-center"><Loader2 size={32} className="animate-spin text-[#b8ff00] mx-auto mb-2" /><p className="text-zinc-400 text-sm">Generating with Sora 2...</p><p className="text-zinc-500 text-xs">~2-5 minutes</p></div>
                  ) : (
                    <div className="text-center"><X size={32} className="text-red-400 mx-auto mb-2" /><p className="text-red-400 text-sm">Generation failed</p></div>
                  )}
                </div>
              )}
              <div className="p-3">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-semibold text-white text-sm">{ad.title || 'Untitled'}</h4>
                    <p className="text-[10px] text-zinc-500 mt-0.5 line-clamp-1">{ad.brief}</p>
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className="text-[10px] px-2 py-0.5 bg-zinc-800 text-zinc-400 rounded-full">{ad.style}</span>
                      <span className="text-[10px] px-2 py-0.5 bg-zinc-800 text-zinc-400 rounded-full">{ad.duration}s</span>
                      <span className="text-[10px] px-2 py-0.5 bg-zinc-800 text-zinc-400 rounded-full">{ad.aspect}</span>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={async () => { await suiteAPI.deleteVideoAd(ad.id); load(); toast.success('Deleted'); }} className="text-zinc-500 hover:text-red-400 h-7 w-7 p-0"><Trash2 size={14} /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════ */
/*  AI COPYWRITER                                                  */
/* ═══════════════════════════════════════════════════════════════ */
function AICopyPanel() {
  const [copies, setCopies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [form, setForm] = useState({ type: 'headline', brief: '', brand_voice: 'professional', target_audience: 'general' });
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(() => { suiteAPI.listCopy().then(r => { setCopies(r.data); setLoading(false); }).catch(() => setLoading(false)); }, []);
  useEffect(() => { load(); }, [load]);

  const handleGenerate = async () => {
    if (!form.brief.trim()) { toast.error('Brief required'); return; }
    setGenerating(true);
    try {
      const res = await suiteAPI.generateCopy(form);
      toast.success('Copy generated!');
      setCopies(prev => [res.data, ...prev]);
      setExpandedId(res.data.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setGenerating(false);
  };

  const COPY_TYPES = [
    { v: 'headline', l: 'Headlines', icon: Type },
    { v: 'email', l: 'Email', icon: Mail },
    { v: 'social', l: 'Social Posts', icon: Share2 },
    { v: 'cta', l: 'CTAs', icon: Target },
    { v: 'tagline', l: 'Taglines', icon: Zap },
    { v: 'ad_script', l: 'Ad Script', icon: Film },
    { v: 'blog_outline', l: 'Blog Outline', icon: FileText },
    { v: 'press_release', l: 'Press Release', icon: Megaphone },
  ];
  const VOICES = ['professional', 'casual', 'bold', 'luxury', 'playful', 'authoritative', 'empathetic', 'urgent'];

  return (
    <div className="p-6 space-y-4" data-testid="suite-ai-copy">
      <div><h2 className="text-xl font-bold text-white">AI Copywriter</h2><p className="text-zinc-500 text-sm">Generate marketing copy instantly</p></div>

      {/* Generator */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 space-y-4">
        <div>
          <label className="text-xs text-zinc-400 block mb-2">Copy Type</label>
          <div className="flex flex-wrap gap-2">
            {COPY_TYPES.map(ct => (
              <button key={ct.v} onClick={() => setForm({ ...form, type: ct.v })} className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 transition-colors ${form.type === ct.v ? 'bg-violet-500 text-white' : 'bg-zinc-800 text-zinc-400 hover:text-white'}`} data-testid={`copy-type-${ct.v}`}>
                <ct.icon size={12} /> {ct.l}
              </button>
            ))}
          </div>
        </div>
        <Textarea value={form.brief} onChange={e => setForm({ ...form, brief: e.target.value })} placeholder="Describe what you're promoting... (product launch, new feature, seasonal sale, event...)" rows={3} className="bg-zinc-800 border-zinc-700 text-white" data-testid="copy-brief-input" />
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-zinc-400 block mb-1">Brand Voice</label>
            <select value={form.brand_voice} onChange={e => setForm({ ...form, brand_voice: e.target.value })} className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm rounded-lg px-3 py-2" data-testid="voice-select">
              {VOICES.map(v => <option key={v} value={v}>{v.charAt(0).toUpperCase() + v.slice(1)}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-zinc-400 block mb-1">Target Audience</label>
            <Input value={form.target_audience} onChange={e => setForm({ ...form, target_audience: e.target.value })} placeholder="e.g. Gen Z, professionals, parents" className="bg-zinc-800 border-zinc-700 text-white" data-testid="audience-input" />
          </div>
        </div>
        <Button onClick={handleGenerate} disabled={generating} className="bg-violet-500 hover:bg-violet-600 text-white font-bold" data-testid="generate-copy-btn">
          {generating ? <><Loader2 size={14} className="animate-spin mr-1" /> Generating...</> : <><Pen size={14} className="mr-1" /> Generate Copy</>}
        </Button>
      </div>

      {/* Results */}
      {loading ? <PanelLoader /> : copies.length === 0 ? (
        <EmptyState icon={Pen} text="No copy generated yet. Try the copywriter above!" />
      ) : (
        <div className="space-y-2">
          {copies.map(c => (
            <div key={c.id} className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden" data-testid={`copy-${c.id}`}>
              <button onClick={() => setExpandedId(expandedId === c.id ? null : c.id)} className="w-full flex items-center justify-between p-3 hover:bg-zinc-800/50 transition-colors">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] px-2 py-0.5 bg-violet-500/20 text-violet-400 rounded-full uppercase font-medium">{c.type}</span>
                  <span className="text-sm text-white truncate max-w-[300px]">{c.brief}</span>
                </div>
                <ChevronRight size={14} className={`text-zinc-500 transition-transform ${expandedId === c.id ? 'rotate-90' : ''}`} />
              </button>
              {expandedId === c.id && (
                <div className="p-4 pt-0 border-t border-zinc-800">
                  <CopyResultDisplay result={c.result} type={c.type} />
                  <div className="flex gap-2 mt-3">
                    <Button variant="ghost" size="sm" onClick={() => { navigator.clipboard.writeText(JSON.stringify(c.result, null, 2)); toast.success('Copied!'); }} className="text-zinc-400 text-xs"><Copy size={12} className="mr-1" /> Copy All</Button>
                    <Button variant="ghost" size="sm" onClick={async () => { await suiteAPI.deleteCopy(c.id); load(); toast.success('Deleted'); }} className="text-zinc-400 text-xs hover:text-red-400"><Trash2 size={12} className="mr-1" /> Delete</Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CopyResultDisplay({ result, type }) {
  if (!result) return <p className="text-zinc-500 text-sm">No result</p>;
  if (typeof result === 'string') return <pre className="text-sm text-zinc-300 whitespace-pre-wrap bg-zinc-800 p-3 rounded-lg">{result}</pre>;
  if (result.raw) return <pre className="text-sm text-zinc-300 whitespace-pre-wrap bg-zinc-800 p-3 rounded-lg">{result.raw}</pre>;

  return (
    <div className="space-y-2 mt-2">
      {result.headlines && result.headlines.map((h, i) => (
        <CopyLine key={i} label={`Headline ${i + 1}`} text={h} isBest={i === result.best_pick} />
      ))}
      {result.taglines && result.taglines.map((t, i) => (
        <CopyLine key={i} label={`Tagline ${i + 1}`} text={t} isBest={i === result.best_pick} />
      ))}
      {result.ctas && result.ctas.map((c, i) => (
        <CopyLine key={i} label={`CTA ${i + 1}`} text={c} isBest={i === result.best_pick} />
      ))}
      {result.subject_line && <CopyLine label="Subject Line" text={result.subject_line} />}
      {result.preview_text && <CopyLine label="Preview" text={result.preview_text} />}
      {result.body_html && <div className="bg-zinc-800 p-3 rounded-lg"><p className="text-[10px] text-zinc-500 mb-1 uppercase">Email Body</p><div className="text-sm text-zinc-300 prose prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: result.body_html }} /></div>}
      {result.cta_text && <CopyLine label="CTA" text={result.cta_text} />}
      {result.twitter && <CopyLine label="Twitter/X" text={result.twitter} />}
      {result.instagram && <CopyLine label="Instagram" text={result.instagram} />}
      {result.linkedin && <CopyLine label="LinkedIn" text={result.linkedin} />}
      {result.facebook && <CopyLine label="Facebook" text={result.facebook} />}
      {result.tiktok && <CopyLine label="TikTok" text={result.tiktok} />}
      {result.hashtags && <div className="flex flex-wrap gap-1">{result.hashtags.map((h, i) => <span key={i} className="text-xs text-[#b8ff00] bg-[#b8ff00]/10 px-2 py-0.5 rounded-full">{h}</span>)}</div>}
      {result.script && <CopyLine label="Script" text={result.script} />}
      {result.scenes && result.scenes.map((s, i) => (
        <div key={i} className="bg-zinc-800 p-2 rounded-lg"><p className="text-[10px] text-zinc-500">Scene {i + 1} — {s.timestamp}</p><p className="text-xs text-zinc-400 mt-0.5">Visual: {s.visual}</p><p className="text-xs text-white mt-0.5">VO: {s.voiceover}</p></div>
      ))}
      {result.title && type === 'blog_outline' && <CopyLine label="Title" text={result.title} />}
      {result.meta_description && <CopyLine label="Meta" text={result.meta_description} />}
      {result.sections && result.sections.map((s, i) => (
        <div key={i} className="bg-zinc-800 p-2 rounded-lg"><p className="text-xs font-semibold text-white">{s.heading}</p>{s.key_points?.map((p, j) => <p key={j} className="text-[10px] text-zinc-400 ml-2">• {p}</p>)}</div>
      ))}
      {result.headline && type === 'press_release' && <CopyLine label="Headline" text={result.headline} />}
      {result.subheadline && <CopyLine label="Subheadline" text={result.subheadline} />}
      {result.body && typeof result.body === 'string' && <CopyLine label="Body" text={result.body} />}
    </div>
  );
}

function CopyLine({ label, text, isBest }) {
  return (
    <div className={`flex items-start gap-2 p-2 rounded-lg ${isBest ? 'bg-[#b8ff00]/10 border border-[#b8ff00]/30' : 'bg-zinc-800'}`}>
      <span className="text-[10px] text-zinc-500 uppercase shrink-0 pt-0.5 w-16">{label}</span>
      <p className="text-sm text-zinc-200 flex-1">{text}</p>
      <button onClick={() => { navigator.clipboard.writeText(text); toast.success('Copied!'); }} className="text-zinc-500 hover:text-white shrink-0"><Copy size={12} /></button>
      {isBest && <span className="text-[9px] px-1.5 py-0.5 bg-[#b8ff00] text-black rounded-full font-bold shrink-0">BEST</span>}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════ */
/*  CONTENT CALENDAR                                               */
/* ═══════════════════════════════════════════════════════════════ */
function CalendarPanel() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ title: '', type: 'post', scheduled_date: '', scheduled_time: '09:00', platform: 'instagram', content: '' });

  const load = useCallback(() => { suiteAPI.listCalendar().then(r => { setEntries(r.data); setLoading(false); }).catch(() => setLoading(false)); }, []);
  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!form.title.trim() || !form.scheduled_date) { toast.error('Title and date required'); return; }
    try {
      await suiteAPI.createCalendarEntry(form);
      toast.success('Entry added');
      setShowAdd(false);
      setForm({ title: '', type: 'post', scheduled_date: '', scheduled_time: '09:00', platform: 'instagram', content: '' });
      load();
    } catch (e) { toast.error('Failed'); }
  };

  const PLATFORMS = ['instagram', 'twitter', 'tiktok', 'youtube', 'linkedin', 'facebook', 'email', 'blog'];
  const TYPES = ['post', 'email', 'video', 'event', 'campaign_launch'];

  return (
    <div className="p-6 space-y-4" data-testid="suite-calendar">
      <div className="flex items-center justify-between">
        <div><h2 className="text-xl font-bold text-white">Content Calendar</h2><p className="text-zinc-500 text-sm">Plan and schedule your content</p></div>
        <Button onClick={() => setShowAdd(true)} className="bg-[#b8ff00] text-black font-bold" data-testid="add-calendar-btn"><Plus size={16} className="mr-1" /> Add Entry</Button>
      </div>

      {showAdd && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 space-y-3">
          <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Content title" className="bg-zinc-800 border-zinc-700 text-white" data-testid="calendar-title-input" />
          <div className="grid grid-cols-4 gap-2">
            <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} className="bg-zinc-800 border border-zinc-700 text-white text-sm rounded-lg px-3 py-2">
              {TYPES.map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
            </select>
            <select value={form.platform} onChange={e => setForm({ ...form, platform: e.target.value })} className="bg-zinc-800 border border-zinc-700 text-white text-sm rounded-lg px-3 py-2">
              {PLATFORMS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <Input type="date" value={form.scheduled_date} onChange={e => setForm({ ...form, scheduled_date: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white" data-testid="calendar-date-input" />
            <Input type="time" value={form.scheduled_time} onChange={e => setForm({ ...form, scheduled_time: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white" />
          </div>
          <Textarea value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} placeholder="Content details..." rows={2} className="bg-zinc-800 border-zinc-700 text-white" />
          <div className="flex gap-2">
            <Button onClick={handleAdd} className="bg-[#b8ff00] text-black font-bold">Add</Button>
            <Button variant="ghost" onClick={() => setShowAdd(false)} className="text-zinc-400">Cancel</Button>
          </div>
        </div>
      )}

      {loading ? <PanelLoader /> : entries.length === 0 ? (
        <EmptyState icon={CalendarDays} text="Calendar is empty. Plan your first piece of content." />
      ) : (
        <div className="space-y-2">
          {entries.map(e => (
            <div key={e.id} className="bg-zinc-900 border border-zinc-800 rounded-xl p-3 flex items-center justify-between" data-testid={`cal-${e.id}`}>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-zinc-800 rounded-xl flex flex-col items-center justify-center shrink-0">
                  <span className="text-[10px] text-zinc-500">{e.scheduled_date?.split('-')[1]}/{e.scheduled_date?.split('-')[2]}</span>
                  <span className="text-xs text-white font-bold">{e.scheduled_time || '--:--'}</span>
                </div>
                <div>
                  <p className="text-sm text-white font-medium">{e.title}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[10px] px-2 py-0.5 bg-zinc-800 text-zinc-400 rounded-full">{e.platform}</span>
                    <span className="text-[10px] px-2 py-0.5 bg-zinc-800 text-zinc-400 rounded-full">{e.type}</span>
                  </div>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={async () => { await suiteAPI.deleteCalendarEntry(e.id); load(); toast.success('Deleted'); }} className="text-zinc-500 hover:text-red-400 h-7 w-7 p-0"><Trash2 size={14} /></Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════ */
/*  ASSET LIBRARY                                                  */
/* ═══════════════════════════════════════════════════════════════ */
function AssetsPanel() {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  const load = useCallback(() => { suiteAPI.listAssets(filter || undefined).then(r => { setAssets(r.data); setLoading(false); }).catch(() => setLoading(false)); }, [filter]);
  useEffect(() => { load(); }, [load]);

  const FILTERS = ['', 'video', 'image', 'copy', 'template'];

  return (
    <div className="p-6 space-y-4" data-testid="suite-assets">
      <div><h2 className="text-xl font-bold text-white">Asset Library</h2><p className="text-zinc-500 text-sm">Store and reuse your generated content</p></div>
      <div className="flex gap-2">
        {FILTERS.map(f => (
          <button key={f || 'all'} onClick={() => setFilter(f)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${filter === f ? 'bg-[#b8ff00] text-black' : 'bg-zinc-800 text-zinc-400 hover:text-white'}`}>
            {f || 'All'}
          </button>
        ))}
      </div>
      {loading ? <PanelLoader /> : assets.length === 0 ? (
        <EmptyState icon={Archive} text="No assets saved yet. Generate video ads or copy and save them here." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {assets.map(a => (
            <div key={a.id} className="bg-zinc-900 border border-zinc-800 rounded-xl p-3" data-testid={`asset-${a.id}`}>
              <div className="flex items-start justify-between">
                <div><p className="text-sm font-medium text-white">{a.name}</p><span className="text-[10px] px-2 py-0.5 bg-zinc-800 text-zinc-400 rounded-full">{a.type}</span></div>
                <Button variant="ghost" size="sm" onClick={async () => { await suiteAPI.deleteAsset(a.id); load(); toast.success('Deleted'); }} className="text-zinc-500 hover:text-red-400 h-7 w-7 p-0"><Trash2 size={14} /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════ */
/*  BRAND KIT                                                      */
/* ═══════════════════════════════════════════════════════════════ */
function BrandKitPanel() {
  const [kit, setKit] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { suiteAPI.getBrandKit().then(r => setKit(r.data)).catch(() => {}); }, []);

  const save = async () => {
    setSaving(true);
    try { await suiteAPI.updateBrandKit(kit); toast.success('Brand kit saved!'); } catch { toast.error('Failed'); }
    setSaving(false);
  };

  if (!kit) return <PanelLoader />;

  return (
    <div className="p-6 space-y-4" data-testid="suite-brand-kit">
      <div className="flex items-center justify-between">
        <div><h2 className="text-xl font-bold text-white">Brand Kit</h2><p className="text-zinc-500 text-sm">Define your brand identity for consistent output</p></div>
        <Button onClick={save} disabled={saving} className="bg-[#b8ff00] text-black font-bold" data-testid="save-brand-kit-btn">
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 space-y-3">
          <h3 className="font-semibold text-white text-sm">Identity</h3>
          <Input value={kit.brand_name} onChange={e => setKit({ ...kit, brand_name: e.target.value })} placeholder="Brand name" className="bg-zinc-800 border-zinc-700 text-white" data-testid="brand-name-input" />
          <Input value={kit.tagline} onChange={e => setKit({ ...kit, tagline: e.target.value })} placeholder="Tagline" className="bg-zinc-800 border-zinc-700 text-white" />
          <Textarea value={kit.voice} onChange={e => setKit({ ...kit, voice: e.target.value })} placeholder="Brand voice description (e.g. Bold, confident, innovative)" rows={2} className="bg-zinc-800 border-zinc-700 text-white" data-testid="brand-voice-input" />
        </div>

        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 space-y-3">
          <h3 className="font-semibold text-white text-sm">Colors</h3>
          <div className="grid grid-cols-2 gap-2">
            {['primary_color', 'secondary_color', 'accent_color', 'dark_color'].map(key => (
              <div key={key} className="flex items-center gap-2">
                <input type="color" value={kit[key] || '#000000'} onChange={e => setKit({ ...kit, [key]: e.target.value })} className="w-8 h-8 rounded-lg border-none cursor-pointer" />
                <div className="flex-1">
                  <p className="text-[10px] text-zinc-500 capitalize">{key.replace('_', ' ')}</p>
                  <p className="text-xs text-white font-mono">{kit[key]}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="flex gap-2 h-12 rounded-xl overflow-hidden">
            <div className="flex-1" style={{ backgroundColor: kit.primary_color }} />
            <div className="flex-1" style={{ backgroundColor: kit.secondary_color }} />
            <div className="flex-1" style={{ backgroundColor: kit.accent_color }} />
            <div className="flex-1" style={{ backgroundColor: kit.dark_color }} />
          </div>
        </div>

        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 space-y-3">
          <h3 className="font-semibold text-white text-sm">Key Messages</h3>
          <Textarea value={(kit.key_messages || []).join('\n')} onChange={e => setKit({ ...kit, key_messages: e.target.value.split('\n').filter(Boolean) })} placeholder="One key message per line" rows={4} className="bg-zinc-800 border-zinc-700 text-white text-sm" />
        </div>

        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 space-y-3">
          <h3 className="font-semibold text-white text-sm">Do / Don't Words</h3>
          <Input value={(kit.do_words || []).join(', ')} onChange={e => setKit({ ...kit, do_words: e.target.value.split(',').map(w => w.trim()).filter(Boolean) })} placeholder="Words to USE (comma separated)" className="bg-zinc-800 border-zinc-700 text-white text-sm" />
          <Input value={(kit.dont_words || []).join(', ')} onChange={e => setKit({ ...kit, dont_words: e.target.value.split(',').map(w => w.trim()).filter(Boolean) })} placeholder="Words to AVOID (comma separated)" className="bg-zinc-800 border-zinc-700 text-white text-sm" />
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════ */
/*  TEAM MANAGEMENT                                                */
/* ═══════════════════════════════════════════════════════════════ */
function TeamPanel({ isSuperadmin }) {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState('');

  const load = useCallback(() => { suiteAPI.listTeam().then(r => { setTeam(r.data); setLoading(false); }).catch(() => setLoading(false)); }, []);
  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!newEmail.trim()) return;
    try {
      await suiteAPI.addTeamMember({ email: newEmail.trim() });
      toast.success(`${newEmail} added to team`);
      setNewEmail('');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleRemove = async (email) => {
    try {
      await suiteAPI.removeTeamMember({ email });
      toast.success(`${email} removed`);
      load();
    } catch (e) { toast.error('Failed'); }
  };

  return (
    <div className="p-6 space-y-4" data-testid="suite-team">
      <div><h2 className="text-xl font-bold text-white">Team Management</h2><p className="text-zinc-500 text-sm">Manage who has access to the Marketing Suite</p></div>

      {isSuperadmin && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
          <h3 className="font-semibold text-white text-sm mb-3">Add Team Member</h3>
          <div className="flex gap-2">
            <Input value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="User's email (must be registered)" className="bg-zinc-800 border-zinc-700 text-white flex-1" data-testid="add-member-email" />
            <Button onClick={handleAdd} className="bg-[#b8ff00] text-black font-bold" data-testid="add-member-btn"><UserPlus size={16} className="mr-1" /> Add</Button>
          </div>
          <p className="text-[10px] text-zinc-500 mt-2">The user must have a registered account first.</p>
        </div>
      )}

      {loading ? <PanelLoader /> : team.length === 0 ? (
        <EmptyState icon={Users} text="No team members yet. Add users by their email." />
      ) : (
        <div className="space-y-2">
          {team.map(m => (
            <div key={m.id} className="bg-zinc-900 border border-zinc-800 rounded-xl p-3 flex items-center justify-between" data-testid={`member-${m.id}`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-zinc-800 rounded-full flex items-center justify-center"><span className="text-white font-bold text-sm">{m.name?.[0] || '?'}</span></div>
                <div><p className="text-sm font-medium text-white">{m.name}</p><p className="text-xs text-zinc-500">{m.email}</p></div>
              </div>
              {isSuperadmin && (
                <Button variant="ghost" size="sm" onClick={() => handleRemove(m.email)} className="text-zinc-500 hover:text-red-400"><UserMinus size={14} /></Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════ */
/*  PLACEHOLDER for existing marketing tools                       */
/* ═══════════════════════════════════════════════════════════════ */
function PlaceholderPanel({ setActiveSection }) {
  return (
    <div className="p-6 text-center py-20" data-testid="suite-placeholder">
      <Settings size={48} className="mx-auto mb-4 text-zinc-600" />
      <h2 className="text-xl font-bold text-white mb-2">Available in Marketing Hub</h2>
      <p className="text-zinc-500 text-sm max-w-md mx-auto mb-4">
        This tool is already built in the Marketing Hub. Access it from the main navigation for full functionality including campaigns, email blasts, A/B testing, social posting, and more.
      </p>
      <Button onClick={() => window.location.href = '/marketing'} className="bg-zinc-800 text-white">
        Open Marketing Hub
      </Button>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════ */
/*  SHARED COMPONENTS                                              */
/* ═══════════════════════════════════════════════════════════════ */
function PanelLoader() {
  return <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-[#b8ff00]" size={24} /></div>;
}

function EmptyState({ icon: Icon, text }) {
  return (
    <div className="text-center py-16 bg-zinc-900 border border-zinc-800 rounded-xl">
      <Icon size={32} className="mx-auto mb-3 text-zinc-600" />
      <p className="text-zinc-500 text-sm">{text}</p>
    </div>
  );
}
